'use client';

import { useBookProStore } from '@/store/useLocaleBookProStore';
import { useTranslations } from 'next-intl';

export default function NavigationButtons({ onDisabledClick, disable = false }) {
  const t = useTranslations('wizard.navigation');
  const { currentStep, formData, processedAuthorImageUrl, goToNextStep, hasAcceptedTerms, setHasSeenEmailScreen, selectedCoverType, selectedFictionCoverHash, fictionCovers } = useBookProStore();

  const canGoNext = () => {
    // Add defensive check for formData
    if (!formData) return false;

    switch (currentStep) {
      case 1:
        return formData.genre && formData.genre.trim() !== '';
      case 2:
        return formData.name && formData.name.trim() !== '';
      case 3:
        // Details/Questions screen
        return formData.questionsAndAnswers && formData.questionsAndAnswers.filter(qa => qa.answer && qa.answer.trim() !== '').length > 0;
      case 4:
        // Title selection screen
        return formData.selectedTitle && formData.selectedTitle.trim() !== '';
      case 5:
        // Email/Consent screen
        const email = formData.email || '';
        if (!email.trim()) {
          return true;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) && hasAcceptedTerms;
      case 6:
        return disable; // Cover type selection (auto-advance, handled by screen)
      case 7:
        return processedAuthorImageUrl && processedAuthorImageUrl.trim() !== '';
      case 8:
        // Cover design screen - check based on cover type
        if (selectedCoverType === 'fiction') {
          // Require a completed fiction cover (not pending/generating)
          if (!selectedFictionCoverHash) return false;
          const selectedCover = fictionCovers?.find(c => c.hashKey === selectedFictionCoverHash);
          return selectedCover?.status === 'success' && !!selectedCover?.url;
        }
        return true; // Non-fiction always has default template selected
      case 9:
        return true; // Allow navigation from preview screen
      case 10:
        return true; // Allow navigation from order screen
      case 11:
        return true; // Screen 11 is the final screen
      default:
        return false;
    }
  };

  const isDisabled = !canGoNext() || currentStep === 11;

  const handleNext = () => {
    if (isDisabled) {
      if (onDisabledClick) onDisabledClick();
      return;
    }

    if (currentStep === 2 && window.stepTwoFormatName) {
      window.stepTwoFormatName();
    }

    if (currentStep === 5) {
      setHasSeenEmailScreen(true);
    }

    goToNextStep();
  };

  // Don't render button on Screen9 (has sticky button), Screen10 and Screen11
  if (currentStep === 9 || currentStep === 10 || currentStep === 11) {
    return null;
  }

  return (
    <div className="w-full mt-4 sm:mt-6 max-w-full sm:max-w-[820px] mx-auto px-6 sm:px-6 md:px-20">
      <button
        className={`md:mt-6 w-full px-4  bg-orange text-white max-w-[330px] h-[55px] mx-auto flex justify-center items-center font-bold text-[19px] leading-[14px] rounded-[7px] gap-1 shadow-heroBtnShadow group relative
          ${isDisabled ? "opacity-60 cursor-not-allowed" : "opacity-100"}`}
        onClick={handleNext}
      >
        <span>{t('next')}</span>
        <span className={`transition duration-300 ${isDisabled ? "group-hover:translate-x-0" : "group-hover:translate-x-1"}`}>
          <svg
            width="18"
            height="14"
            viewBox="0 0 18 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_0_615)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.82983 0.670075C2.29032 0.209585 3.03692 0.209585 3.49741 0.670073L9.61156 6.78422C9.83269 7.00535 9.95692 7.30528 9.95692 7.618C9.95692 7.93074 9.83269 8.23067 9.61156 8.4518L3.49741 14.5659C3.03692 15.0265 2.29032 15.0265 1.82983 14.5659C1.36934 14.1054 1.36934 13.3589 1.82983 12.8984L7.11019 7.618L1.82983 2.33765C1.36934 1.87716 1.36934 1.13056 1.82983 0.670075Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.9938 0.670075C9.45429 0.209585 10.2009 0.209585 10.6614 0.670073L16.7755 6.78422C16.9966 7.00535 17.1208 7.30528 17.1208 7.618C17.1208 7.93074 16.9966 8.23067 16.7755 8.4518L10.6614 14.5659C10.2009 15.0265 9.45429 15.0265 8.9938 14.5659C8.53332 14.1054 8.53332 13.3589 8.9938 12.8984L14.2742 7.618L8.9938 2.33765C8.53332 1.87716 8.53332 1.13056 8.9938 0.670075Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="clip0_0_615">
                <rect
                  width="17.0323"
                  height="15.7221"
                  fill="white"
                  transform="translate(0.648438 0.193359)"
                />
              </clipPath>
            </defs>
          </svg>
        </span>
      </button>
    </div>
  );
}